/**
 * Canvas plumbing, shared by everything that rasterises.
 *
 * `OffscreenCanvas` is the right tool — it works off the main thread and its
 * `convertToBlob` is a promise rather than a callback — but it is not universal,
 * and a console that throws `OffscreenCanvas is not defined` while a guest is
 * waiting is worse than one that draws a fraction slower. Every canvas in the
 * app goes through here so the fallback exists in one place instead of three
 * near-identical copies that can drift.
 *
 * This lives in `core/` because it is framework-free and because the compositor
 * — the part that must stay verifiable in isolation — depends on it.
 */

export type AnyCanvas = OffscreenCanvas | HTMLCanvasElement

export function makeCanvas(width: number, height: number): AnyCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

export interface ContextOptions {
  /**
   * Bicubic-quality resampling, matching the HighQualityBicubic / HighQuality
   * settings on the WPF Graphics the compositor was ported from. Off by default
   * because the browser default is `low`, and only the paths that were
   * high-quality in the original should pay for it.
   */
  highQuality?: boolean
}

export function canvasContext(
  canvas: AnyCanvas,
  options: ContextOptions = {},
): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null
  if (!ctx) throw new Error('canvas: 2d context unavailable')

  if (options.highQuality) {
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
  }
  return ctx
}

export function toBlob(canvas: AnyCanvas, type = 'image/png', quality?: number): Promise<Blob> {
  if ('convertToBlob' in canvas) return canvas.convertToBlob({ type, quality })
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
      type,
      quality,
    ),
  )
}

/**
 * Decodes an image, honouring its EXIF orientation.
 *
 * `imageOrientation: 'from-image'` is the whole point — a portrait frame the
 * camera tagged with EXIF 6 has to come back upright, because orientation is
 * what decides which layout slot a photo can go in. The options bag is not
 * universally supported, so a browser that rejects it falls back to a plain
 * decode rather than failing: an unrotated thumbnail is a cosmetic problem, a
 * gallery that will not load is not.
 */
export async function decodeBitmap(
  source: Blob,
  options: ImageBitmapOptions = {},
): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(source, { imageOrientation: 'from-image', ...options })
  } catch {
    return createImageBitmap(source)
  }
}
